// src/contexts/CampaignContext.tsx
'use client';

import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useHttp } from 'src/hooks';
import { AuthContext } from './auth-context';
import { Campaign } from 'src/types/campaign';

interface CampaignContextType {
  campaigns: Campaign[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  fetchCampaigns: () => Promise<void>;
  setCurrentPage: (page: number) => void;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

export const CampaignProvider = ({ children }: { children: ReactNode }) => {
  const { sendRequest, isLoading } = useHttp();
  const { accessToken, profile } = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const fetchCampaigns = async () => {
    if (!accessToken || profile?.facebook == null) return;

    try {
      await sendRequest(
        {
          url: `campaign`,
          method: 'GET',
          accessToken,
        },
        (data: Campaign[]) => {
          setCampaigns(data || []);
        },
      );
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      setCampaigns([]);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchCampaigns();
    }
  }, [accessToken, profile]);

  return (
    <CampaignContext.Provider
      value={{
        campaigns,
        isLoading,
        currentPage,
        pageSize,
        totalItems: campaigns.length,
        fetchCampaigns,
        setCurrentPage,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
};

export const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (context === undefined) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return context;
};
